import { Controller, useFormContext } from 'react-hook-form';
import { Box, MenuItem, TextField as MuiTextField, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CurrencyInput from './shared/CurrencyInput';
import { fmtPhone, formatPhoneInputValue } from '../utils/formatters';

const toDateInputValue = (val) => {
  if (!val) return '';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const fmtCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '-';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '-';
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const EditableField = ({
  label,
  value,
  name,
  onChange,
  isEditMode,
  required = false,
  highlight = false,
  error = null,
  type = 'text',
  placeholder = '',
  disabled = false,
  options = null,
  /** Passed to CurrencyInput: fixed fractional digits (e.g. 2) on blur and when not focused */
  decimalPlaces,
}) => {
  // Must call useFormContext unconditionally (Rules of Hooks)
  const { control } = useFormContext();

  if (!isEditMode) {
    const isEmpty = value === null || value === undefined || value === '';
    const shouldHighlight = highlight || (required && isEmpty);
    // When options are provided, prefer the human label for the matching value (e.g. "12" not "_12").
    const optionLabel = !isEmpty && Array.isArray(options) ? options.find(o => o.value === value)?.label : null;
    const displayVal = optionLabel
      ?? (type === 'currency' ? fmtCurrency(value) : type === 'tel' ? (value ? fmtPhone(value) : '-') : type === 'date' && value ? new Date(value).toLocaleDateString() : (value || '-'));
    return (
      <Box sx={shouldHighlight ? { bgcolor: 'highlight.field', borderRadius: 1, px: 1, py: 0.25 } : { py: 0.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {label}
          {shouldHighlight && <WarningAmberIcon sx={{ fontSize: 11, ml: 0.5, color: 'warning.main' }} />}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{displayVal}</Typography>
      </Box>
    );
  }

  if (options) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const isEmpty = field.value === null || field.value === undefined || field.value === '';
          const shouldHighlight = highlight || (required && isEmpty);
          const highlightSx = shouldHighlight ? { '& .MuiOutlinedInput-root': { bgcolor: 'highlight.field' } } : {};
          return (
            <MuiTextField
              select
              fullWidth
              size="small"
              label={label}
              required={required}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value);
                onChange?.(e.target.value);
              }}
              onBlur={field.onBlur}
              disabled={disabled}
              error={!!fieldState.error || !!error}
              helperText={fieldState.error?.message || error || undefined}
              sx={{ mb: 2, ...highlightSx }}
            >
              <MenuItem value="">Select {label}...</MenuItem>
              {options.map(opt => (
                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled === true}>{opt.label}</MenuItem>
              ))}
            </MuiTextField>
          );
        }}
      />
    );
  }

  if (type === 'currency') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const isEmpty = field.value === null || field.value === undefined || field.value === '';
          const shouldHighlight = highlight || (required && isEmpty);
          const highlightSx = shouldHighlight ? { '& .MuiOutlinedInput-root': { bgcolor: 'highlight.field' } } : {};
          return (
            <CurrencyInput
              fullWidth
              size="small"
              label={label}
              required={required}
              value={field.value}
              onChange={(v) => {
                field.onChange(v);
                onChange?.(v);
              }}
              onBlur={field.onBlur}
              decimalPlaces={decimalPlaces}
              placeholder={placeholder}
              disabled={disabled}
              error={!!fieldState.error || !!error}
              helperText={fieldState.error?.message || error || undefined}
              sx={{ mb: 2, ...highlightSx }}
            />
          );
        }}
      />
    );
  }

  // text, date, tel, number
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const isEmpty = field.value === null || field.value === undefined || field.value === '';
        const shouldHighlight = highlight || (required && isEmpty);
        const highlightSx = shouldHighlight ? { '& .MuiOutlinedInput-root': { bgcolor: 'highlight.field' } } : {};
        const displayValue = type === 'tel' ? formatPhoneInputValue(field.value) : type === 'date' ? toDateInputValue(field.value) : (field.value ?? '');
        return (
          <MuiTextField
            fullWidth
            size="small"
            type={type}
            label={label}
            required={required}
            value={displayValue}
            onChange={(e) => {
              const v = type === 'tel' ? formatPhoneInputValue(e.target.value) : e.target.value;
              field.onChange(v);
              onChange?.(v);
            }}
            onBlur={field.onBlur}
            placeholder={type === 'tel' && !placeholder ? '(555) 123-4567' : placeholder}
            disabled={disabled}
            error={!!fieldState.error || !!error}
            helperText={fieldState.error?.message || error || undefined}
            slotProps={type === 'date' ? { inputLabel: { shrink: true } } : undefined}
            sx={{ mb: 2, ...highlightSx }}
          />
        );
      }}
    />
  );
};

export default EditableField;
